import React, { memo, useMemo } from 'react'
import { Message } from 'primereact/message'

import { LineChart } from '../../../LineChart'
import {
    ChargingSession,
    Consumption,
    useChargePointGetCalibrationQuery,
    useConsumptionDataChargingSessionQuery,
    useChargingSessionGetConsumptionForSessionQuery,
    ChargePoint,
    ConnectorPhaseRotations,
} from '../../../../redux/api/web-api'
import localizeDate from '../../../../utils/localizeDate'
import Text from '../../../../../ui/Text'
import useText from '../../../../hooks/useText'
import { Dialog } from '../../../../uikit/Dialog'

export type ConsumptionGraphDialogProps = {
    visible: boolean
    onHide: () => void
    session?: ChargingSession
    chargePoint: ChargePoint
    connectorPhaseRotation?: ConnectorPhaseRotations
}

export const ConsumptionGraphDialog = memo(
    ({ visible, onHide, session, chargePoint, connectorPhaseRotation }: ConsumptionGraphDialogProps) => {
        const text = useText()

        const { data: legacyConsumptionData, isFetching: isFetchingLegacy } =
            useChargingSessionGetConsumptionForSessionQuery(
                { id: Number(session?.id) },
                { skip: !session?.id || chargePoint.isOcpp, refetchOnMountOrArgChange: true }
            )

        const { data: ocppConsumptionData, isFetching: isFetchingOcpp } = useConsumptionDataChargingSessionQuery(
            { id: Number(session?.id) },
            { skip: !session?.id || !chargePoint.isOcpp, refetchOnMountOrArgChange: true }
        )

        const isFetching = chargePoint.isOcpp ? isFetchingOcpp : isFetchingLegacy
        const rawData = useMemo(() => {
            if (chargePoint.isOcpp) {
                return (ocppConsumptionData?.data ?? []).map((item) => ({
                    timestamp: item.bucketStartUtc,
                    chargingCurrent: item.currentL1A ?? undefined,
                    chargingCurrent2: item.currentL2A ?? undefined,
                    chargingCurrent3: item.currentL3A ?? undefined,
                    chargingVoltage: item.voltageL1V ?? undefined,
                    chargingVoltage2: item.voltageL2V ?? undefined,
                    chargingVoltage3: item.voltageL3V ?? undefined,
                }))
            }

            return (legacyConsumptionData || []) as Consumption[]
        }, [chargePoint.isOcpp, legacyConsumptionData, ocppConsumptionData])

        const hasMeasurements = rawData.some((item) =>
            [
                item.chargingCurrent,
                item.chargingCurrent2,
                item.chargingCurrent3,
                item.chargingVoltage,
                item.chargingVoltage2,
                item.chargingVoltage3,
            ].some(Number.isFinite)
        )

        const { data: calibrationData } = useChargePointGetCalibrationQuery(
            { chargePointId: session?.chargePointId || '' },
            { skip: !session?.chargePointId || session?.productId !== 1 }
        )

        const { currentData, voltageData } = useMemo(() => {
            const currentData1 = []
            const currentData2 = []
            const currentData3 = []

            const voltageData1 = []
            const voltageData2 = []
            const voltageData3 = []

            for (const item of rawData) {
                const currentAdjust = calibrationData?.currentAdjust || 1
                const voltageAdjust = calibrationData?.voltageAdjust || 1
                const x = new Date(item.timestamp ?? '').valueOf()

                currentData1.push({ y: (item.chargingCurrent ?? NaN) * currentAdjust, x })
                voltageData1.push({ y: (item.chargingVoltage ?? NaN) * voltageAdjust, x })

                currentData2.push({ y: (item.chargingCurrent2 ?? NaN) * currentAdjust, x })
                voltageData2.push({ y: (item.chargingVoltage2 ?? NaN) * voltageAdjust, x })

                currentData3.push({ y: (item.chargingCurrent3 ?? NaN) * currentAdjust, x })
                voltageData3.push({ y: (item.chargingVoltage3 ?? NaN) * voltageAdjust, x })
            }

            return {
                voltageData: {
                    datasets: [
                        {
                            label: `L1 ${text('lblVoltage')}`,
                            data: voltageData1,
                        },
                        {
                            label: `L2 ${text('lblVoltage')}`,
                            data: voltageData2,
                        },
                        {
                            label: `L3 ${text('lblVoltage')}`,
                            data: voltageData3,
                        },
                    ],
                },
                currentData: {
                    datasets: [
                        {
                            label: `L1 ${text('lblCurrent')}`,
                            data: currentData1,
                        },
                        {
                            label: `L2 ${text('lblCurrent')}`,
                            data: currentData2,
                        },
                        {
                            label: `L3 ${text('lblCurrent')}`,
                            data: currentData3,
                        },
                    ],
                },
            }
        }, [calibrationData?.currentAdjust, calibrationData?.voltageAdjust, rawData, text])

        const renderContent = () => {
            if (isFetching) {
                return (
                    <div className="flex flex-column gap-8 h-full">
                        <div className="shimmer-container shimmer-medium h-full" />
                        <div className="shimmer-container shimmer-medium h-full" />
                    </div>
                )
            }

            if (!hasMeasurements) {
                return (
                    <div className="h-3rem w-full flex justify-content-center align-items-center text-base m-auto">
                        <Text code="msgNoAvailableData" />
                    </div>
                )
            }
            return (
                <>
                    <div className="flex-1">
                        <LineChart
                            yAxisLabel={text('lblCurrentAxis')}
                            xAxisLabel={text('lblTime')}
                            data={currentData}
                            className="h-full"
                        />
                    </div>
                    <div className="flex-1">
                        <LineChart
                            yAxisLabel={text('lblVoltageAxis')}
                            xAxisLabel={text('lblTime')}
                            data={voltageData}
                            className="h-full"
                        />
                    </div>
                </>
            )
        }

        return (
            <Dialog
                header={text('lblConsumption')}
                visible={visible}
                onHide={onHide}
                style={{ width: '75vw', maxWidth: '90rem', height: '100vh', maxHeight: '80rem' }}
                pt={{
                    content: {
                        className: 'overflow-x-hidden',
                    },
                }}
            >
                <div className="flex flex-column px-2">
                    <div className="grid mb-3">
                        <div className="col-12 md:col">
                            <div className="text-2xl mb-1">
                                <Text code="lblDateInterval" />
                            </div>
                            <strong className="text-2xl">
                                {session
                                    ? `${localizeDate(session.startTime)} - ${localizeDate(session.endTime || '')}`
                                    : ''}
                            </strong>
                        </div>
                        <div className="col-12 md:col text-right">
                            <div className="text-2xl mb-1">
                                <Text code="lblTotalConsumptionKWh" />
                            </div>
                            <strong className="text-2xl">{session?.totalConsumptionKwh?.toFixed(3)} kWh</strong>
                        </div>
                    </div>

                    <div className="flex flex-column flex-1 justify-content-start">{renderContent()}</div>

                    {chargePoint.isOcpp && connectorPhaseRotation !== 'RST' && (
                        <Message
                            severity="info"
                            text={<Text code="lblMeterValuePhaseRotationInfoText" />}
                            className="block"
                        />
                    )}
                </div>
            </Dialog>
        )
    }
)
